import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useQueryClient } from '@tanstack/react-query';

import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { GoogleAddressAutocomplete } from '@/components/ui/GoogleAddressAutocomplete';
import { getPatient, updatePatient } from '@/api/patients';

// =============================================================================
// VALIDATION SCHEMA
// =============================================================================

const patientSchema = z.object({
  first_name: z.string().min(1, 'First name is required').max(100),
  last_name: z.string().min(1, 'Last name is required').max(100),
  date_of_birth: z.string().optional().refine(
    (val) => {
      if (!val) return true;
      const dob = new Date(val);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear() - 
        ((today.getMonth() < dob.getMonth() || 
          (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) ? 1 : 0);
      return age >= 18;
    },
    { message: 'Patient must be 18 years or older' }
  ),
  gender: z.enum(['male', 'female']).optional(),
  email: z.string().email('Invalid email format').optional().or(z.literal('')),
  phone_country: z.string().default('+1'),
  phone_primary: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        const digits = val.replace(/\D/g, '');
        return digits.length === 10;
      },
      { message: 'Phone must be 10 digits' }
    ),
  address_line1: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postal_code: z.string().optional(),
  country: z.string().optional(),
});

type PatientFormData = z.infer<typeof patientSchema>;

// =============================================================================
// COMPONENT
// =============================================================================

const EditPatient: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PatientFormData>({
    resolver: zodResolver(patientSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: undefined,
      email: '',
      phone_country: '+1',
      phone_primary: '',
      address_line1: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
    },
  });

  useEffect(() => {
    const loadPatient = async () => {
      if (!patientId) return;
      try {
        const patient = await getPatient(patientId, false);
        // Extract phone country code and number
        let phoneCountry = '+1';
        let phoneNumber = '';
        
        // If mobile_code exists (new format), add '+' prefix for display
        if (patient.mobile_code) {
          phoneCountry = patient.mobile_code.startsWith('+') ? patient.mobile_code : `+${patient.mobile_code}`;
        }
        
        if (patient.phone_primary) {
          // If phone_primary contains country code (old format), extract it
          if (patient.phone_primary.startsWith('+')) {
            const match = patient.phone_primary.match(/^(\+\d+)(.+)$/);
            if (match) {
              phoneCountry = match[1];
              phoneNumber = match[2].replace(/\D/g, '');
            }
          } else {
            // New format - phone_primary is just the number
            phoneNumber = patient.phone_primary.replace(/\D/g, '');
          }
        }
        
        reset({
          first_name: patient.first_name,
          last_name: patient.last_name,
          date_of_birth: patient.date_of_birth || '',
          gender: patient.gender as any,
          email: patient.email || '',
          phone_country: phoneCountry,
          phone_primary: phoneNumber ? formatPhoneNumber(phoneNumber) : '',
          address_line1: patient.address_line1 || '',
          city: patient.city || '',
          state: patient.state || '',
          postal_code: patient.postal_code || '',
          country: patient.country || '',
        });
      } catch (error) {
        toast.error('Failed to load patient');
        navigate('/patients');
      } finally {
        setLoading(false);
      }
    };
    loadPatient();
  }, [patientId, reset, navigate]);

  // Watch address field for manual changes
  const addressLine1Value = watch('address_line1') || '';

  // Handle place selection from Google autocomplete
  const handlePlaceSelected = (place: {
    address: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  }) => {
    // Auto-fill the address fields
    setValue('address_line1', place.address);
    setValue('city', place.city);
    setValue('state', place.state);
    setValue('postal_code', place.postalCode);
    setValue('country', place.country);
  };

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const onSubmit = async (data: PatientFormData) => {
    if (!patientId) return;
    setSubmitting(true);
    try {
      const phoneValue = data.phone_primary?.replace(/\D/g, '');

      const payload: any = {
        first_name: data.first_name,
        last_name: data.last_name,
      };

      if (data.date_of_birth) payload.date_of_birth = data.date_of_birth;
      if (data.gender) payload.gender = data.gender;
      // DO NOT include email in update payload
      if (phoneValue) {
        payload.phone_primary = phoneValue;
        // Remove '+' sign from mobile code for backend storage
        payload.mobile_code = (data.phone_country || '+1').replace('+', '');
      }
      if (data.address_line1) payload.address_line1 = data.address_line1;
      if (data.city) payload.city = data.city;
      if (data.state) payload.state = data.state;
      if (data.postal_code) payload.postal_code = data.postal_code;
      if (data.country) payload.country = data.country;

      await updatePatient(patientId, payload);
      toast.success('Patient updated successfully');
      // Invalidate queries to refresh patient data
      await queryClient.invalidateQueries({ queryKey: ['patients'] });
      await queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
      navigate(`/patients/${patientId}`);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.detail || 'Failed to update patient';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/patients/${patientId}`)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Patient</h1>
          <p className="text-gray-600">Update patient information</p>
        </div>
      </div>

      {/* Form */}
      <Card className="p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First Name"
                {...register('first_name')}
                error={errors.first_name?.message}
                required
              />
              <Input
                label="Last Name"
                {...register('last_name')}
                error={errors.last_name?.message}
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <Controller
                  name="date_of_birth"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      selected={field.value ? new Date(field.value) : null}
                      onChange={(date: Date | null) => {
                        return field.onChange(date ? date.toISOString().split('T')[0] : '');
                      }}
                      dateFormat="MM/dd/yyyy"
                      placeholderText="Select date of birth"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  )}
                />
                {errors.date_of_birth && (
                  <p className="mt-1 text-sm text-red-600">{errors.date_of_birth.message}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Must be 18 years or older
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender
                </label>
                <select
                  {...register('gender')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
                {errors.gender && (
                  <p className="mt-1 text-sm text-red-600">{errors.gender.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Contact Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-gray-500">(Read-only)</span>
                </label>
                <input
                  type="email"
                  {...register('email')}
                  disabled
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Email cannot be changed
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <div className="flex gap-2">
                  <select
                    {...register('phone_country')}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+1">🇨🇦 +1</option>
                  </select>
                  <Controller
                    name="phone_primary"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        value={field.value || ''}
                        type="tel"
                        placeholder="(555) 123-4567"
                        onChange={(e) => {
                          const formatted = formatPhoneNumber(e.target.value);
                          field.onChange(formatted);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    )}
                  />
                </div>
                {errors.phone_primary && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.phone_primary.message}
                  </p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Enter 10-digit phone number
                </p>
              </div>
            </div>
          </div>

          {/* Address (Optional) */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Address <span className="text-sm font-normal text-gray-500">(Optional)</span>
            </h2>
            <div className="space-y-4">
              <GoogleAddressAutocomplete
                label="Street Address"
                value={addressLine1Value}
                onChange={(value) => setValue('address_line1', value)}
                onPlaceSelected={handlePlaceSelected}
                error={errors.address_line1?.message}
                placeholder="Start typing to search address..."
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="City"
                  {...register('city')}
                  error={errors.city?.message}
                />
                <Input
                  label="State"
                  {...register('state')}
                  error={errors.state?.message}
                  placeholder="CA"
                />
                <Input
                  label="Zip Code"
                  {...register('postal_code')}
                  error={errors.postal_code?.message}
                  placeholder="12345"
                />
              </div>
              <Input
                label="Country"
                {...register('country')}
                error={errors.country?.message}
                placeholder="Auto-filled from address"
                readOnly
                className="bg-gray-50"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/patients/${patientId}`)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={submitting}>
              Save Changes
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default EditPatient;
